import { Component, Input, signal, inject, PLATFORM_ID, OnInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';

export interface InlinePromotionData {
  id: string;
  type: 'promotion' | 'ad' | 'announcement';
  title?: string;
  description?: string;
  ctaText?: string;
  ctaLink?: string;
  externalLink?: boolean;
  style?: 'brand' | 'gold' | 'urgent' | 'subtle' | 'custom';
  /** Image/banner URL for ads */
  mediaUrl?: string;
  /** Raw HTML for custom ads (AdSense, etc.) */
  htmlContent?: string;
  /** Custom background gradient or color */
  customBg?: string;
  dismissible?: boolean;
  /** Badge text, e.g., "Limited Offer", "Sponsored" */
  badge?: string;
}

@Component({
  selector: 'app-inline-promotion',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (!dismissed()) {
      <!-- Custom HTML Ad (e.g., AdSense) -->
      @if (promo.type === 'ad' && promo.htmlContent) {
        <div class="py-8" [attr.aria-label]="'Advertisement'" role="complementary">
          <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="relative rounded-2xl overflow-hidden border border-surface-100 bg-surface-50 p-4">
              <span class="absolute top-2 right-3 text-[0.6rem] font-semibold uppercase tracking-wider text-surface-400">Sponsored</span>
              <div [innerHTML]="promo.htmlContent"></div>
            </div>
          </div>
        </div>
      }
      <!-- Image Banner Ad -->
      @else if (promo.type === 'ad' && promo.mediaUrl) {
        <div class="py-8" [attr.aria-label]="'Advertisement: ' + (promo.title || '')" role="complementary">
          <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="relative rounded-2xl overflow-hidden border border-surface-100">
              <span class="absolute top-3 right-4 z-10 px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-white/70 bg-black/30 rounded-full backdrop-blur-sm">Ad</span>
              @if (promo.ctaLink) {
                <a [href]="promo.externalLink ? promo.ctaLink : null"
                   [routerLink]="!promo.externalLink ? promo.ctaLink : null"
                   [target]="promo.externalLink ? '_blank' : undefined"
                   [rel]="promo.externalLink ? 'noopener noreferrer sponsored' : null"
                   class="block">
                  <img [src]="promo.mediaUrl" [alt]="promo.title || 'Advertisement'" class="w-full h-auto rounded-2xl" loading="lazy" />
                </a>
              } @else {
                <img [src]="promo.mediaUrl" [alt]="promo.title || 'Advertisement'" class="w-full h-auto rounded-2xl" loading="lazy" />
              }
              @if (promo.dismissible !== false) {
                <button (click)="dismiss()" aria-label="Dismiss ad"
                        class="absolute top-3 left-4 z-10 p-1.5 rounded-full bg-black/30 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/50 transition-colors">
                  <svg aria-hidden="true" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              }
            </div>
          </div>
        </div>
      }
      <!-- Branded Promotion / Announcement -->
      @else {
        <section class="py-10 md:py-14 relative overflow-hidden"
                 [class]="sectionClasses()"
                 [style.background]="promo.customBg || null"
                 role="complementary"
                 [attr.aria-label]="promo.type === 'announcement' ? 'Announcement' : 'Special offer'">

          <!-- Decorative pattern -->
          <div class="absolute inset-0 pointer-events-none opacity-[0.04]" aria-hidden="true"
               style="background-image: url('data:image/svg+xml,%3Csvg width=&quot;60&quot; height=&quot;60&quot; viewBox=&quot;0 0 60 60&quot; xmlns=&quot;http://www.w3.org/2000/svg&quot;%3E%3Cpath d=&quot;M30 0L60 30L30 60L0 30z&quot; fill=&quot;%23fff&quot; fill-opacity=&quot;.5&quot;/%3E%3C/svg%3E'); background-size: 30px 30px;">
          </div>

          <div class="mx-auto max-w-5xl px-6 lg:px-8 text-center relative z-10">
            @if (promo.badge) {
              <span class="inline-flex items-center px-3 py-1 rounded-full text-[0.65rem] font-bold uppercase tracking-wider mb-4"
                    [class]="badgeClasses()">
                {{ promo.badge }}
              </span>
            }

            @if (promo.title) {
              <h2 class="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-3"
                  [class]="titleClasses()">
                {{ promo.title }}
              </h2>
            }

            @if (promo.description) {
              <p class="max-w-2xl mx-auto mb-7 leading-relaxed"
                 [class]="descClasses()">
                {{ promo.description }}
              </p>
            }

            @if (promo.ctaText && promo.ctaLink) {
              <div class="flex flex-wrap items-center justify-center gap-4">
                @if (promo.externalLink) {
                  <a [href]="promo.ctaLink" target="_blank" rel="noopener noreferrer"
                     class="inline-flex items-center gap-2 px-7 py-3 font-semibold text-sm rounded-lg transition-all duration-300"
                     [class]="ctaBtnClasses()">
                    {{ promo.ctaText }}
                    <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </a>
                } @else {
                  <a [routerLink]="promo.ctaLink"
                     class="inline-flex items-center gap-2 px-7 py-3 font-semibold text-sm rounded-lg transition-all duration-300"
                     [class]="ctaBtnClasses()">
                    {{ promo.ctaText }}
                    <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </a>
                }
              </div>
            }
          </div>

          @if (promo.dismissible) {
            <button (click)="dismiss()" aria-label="Dismiss promotion"
                    class="absolute top-4 right-4 p-1.5 rounded-full transition-colors z-20"
                    [class]="dismissBtnClasses()">
              <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          }
        </section>
      }
    }
  `,
  styles: [`:host { display: block; }`],
})
export class InlinePromotionComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);

  @Input({ required: true }) promo!: InlinePromotionData;

  dismissed = signal(false);

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (sessionStorage.getItem(`inline-promo-dismissed-${this.promo.id}`)) {
        this.dismissed.set(true);
      }
    }
  }

  dismiss() {
    this.dismissed.set(true);
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(`inline-promo-dismissed-${this.promo.id}`, '1');
    }
  }

  sectionClasses() {
    switch (this.promo.style) {
      case 'gold':    return 'bg-gradient-to-r from-gold-600 via-gold-500 to-gold-600';
      case 'urgent':  return 'bg-gradient-to-r from-red-700 via-red-600 to-red-700';
      case 'subtle':  return 'bg-surface-50 border-y border-surface-100';
      case 'custom':  return '';
      case 'brand':
      default:        return 'bg-gradient-to-br from-brand-800 via-brand-700 to-brand-800';
    }
  }

  titleClasses() {
    return this.promo.style === 'subtle' ? 'text-surface-900' : 'text-white';
  }

  descClasses() {
    return this.promo.style === 'subtle' ? 'text-surface-500' : 'text-white/80';
  }

  badgeClasses() {
    switch (this.promo.style) {
      case 'urgent':  return 'bg-white/20 text-white';
      case 'subtle':  return 'bg-brand-100 text-brand-700';
      default:        return 'bg-white/15 text-white';
    }
  }

  ctaBtnClasses() {
    switch (this.promo.style) {
      case 'gold':    return 'bg-white text-gold-700 hover:bg-white/90 hover:shadow-lg';
      case 'urgent':  return 'bg-white text-red-700 hover:bg-white/90 hover:shadow-lg';
      case 'subtle':  return 'bg-brand-600 text-white hover:bg-brand-700 hover:shadow-lg';
      case 'brand':
      default:        return 'bg-white text-brand-800 hover:bg-white/90 hover:shadow-lg hover:shadow-white/10';
    }
  }

  dismissBtnClasses() {
    return this.promo.style === 'subtle'
      ? 'text-surface-400 hover:bg-surface-200'
      : 'text-white/60 hover:bg-white/15 hover:text-white';
  }
}
